import { Grid, Typography } from '@mui/material';
import React from 'react';
import styles from './styles.module.css';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  ModelSetHolding,
  ModelSetPerformance,
} from 'features/premium-report/api/types';
import { Slide } from '../../../_data';
import { getSlideField } from '../../../utils';
import { SlideParagraph } from '../slide-paragraph';
import { SlideSubtitle } from '../slide-subtitle/slide-subtitle';

export type PerformanceDisclosuresProps = [
  {
    field: 'rows';
    label: string;
    value: {
      id: string;
      full_name: string;
      index_proxy: string;
    }[];
    type: 'rows';
  },
  {
    field: 'performance_disclosures';
    label: string;
    value: string;
    type: 'performance_disclosures';
  },
  {
    field: 'profile';
    label: string;
    value: number;
    type: 'number';
  },
];

export const PerformanceDisclosures = React.memo(
  ({
    slide,
  }: {
    slide: Slide<PerformanceDisclosuresProps>;
  }): React.ReactElement => {
    const performance_metadata = getSlideField(
      slide,
      'performance_disclosures',
    )?.value;
    const end_date = performance_metadata[0].performance_metadata.end_date;
    const profile = getSlideField(slide, 'profile')?.value || 3;
    console.log(profile, 'profile');
    return (
      <Grid container direction="column" className={styles.container}>
        <Grid item>
          <SlideSubtitle>Back-tested Performance Results:</SlideSubtitle>
          <SlideParagraph>
            The following pages provide charts illustrating rolling period
            excess returns and risk for the Profile {profile} model and analysis
            of the results. Performance for the other model risk profiles are
            included in the supplemental performance reports.
          </SlideParagraph>
          <SlideParagraph>
            All returns are gross of fees and as of {end_date}. Please see
            AssetMark factsheets that contain standardized performance periods
            and net of fees returns information.
          </SlideParagraph>
          <SlideParagraph>
            All portfolios use annual rebalancing, while the Global Index blends
            use monthly rebalancing. Global Blend is a mix of MSCI ACWI and
            Bloomberg Global Aggregate indexes.
          </SlideParagraph>
          <SlideParagraph>
            Strategist performance returns are used since the inception of the
            strategy which may include model and composite performance returns.
            Index/blend of indices is used prior to inception of the strategy.
          </SlideParagraph>
          <Grid item xs={12} margin="10px 0">
            <DataGrid
              rows={getSlideField(slide, 'rows')?.value}
              columns={columns}
              disableColumnMenu
              disableColumnFilter
              hideFooter
              disableColumnSorting
              disableColumnSelector
              disableMultipleRowSelection
              disableRowSelectionOnClick
              rowHeight={44}
              sx={{
                height: 'fit-content',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#f0f0f0',
                  whiteSpace: 'normal',
                  height: 'auto',
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    whiteSpace: 'normal',
                    lineHeight: 'normal',
                    height: 'auto',
                  },
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '14px',
                  whiteSpace: 'normal !important',
                  lineHeight: 'normal',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Grid>
          <SlideParagraph>
            Index and Model returns use software programs to track investment
            portfolios, which are considered hypothetical performance and do not
            reflect actual performance.
          </SlideParagraph>
          <SlideParagraph>
            Composite returns are based on actual investor portfolios.
          </SlideParagraph>

          <SlideParagraph
            style={{ marginTop: '10px', fontSize: '15px', fontWeight: '500' }}
          >
            There are inherent limitations in the use of hypothetical
            performance and Proxy Index information, including
          </SlideParagraph>
          <SlideParagraph>
            <ol style={{ fontSize: '13px', fontWeight: '500' }}>
              <li style={{ marginBottom: '4px' }}>
                There is no guarantee that the return objective of any
                investment strategy will be achieved.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Does not reflect actual client performance or investment
                decisions.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Actual performance of your account will differ from the
                hypothetical performance presented. Hypothetical information
                represents the allocation and performance of an index or group
                of indices, or Model Performance, and is included as a general
                illustration of how your recommended portfolio might perform
                over time.
              </li>
              <li style={{ marginBottom: '4px' }}>
                There is no actual trading that occurs within a Proxy Index.
              </li>
              <li style={{ marginBottom: '4px' }}>
                It is not possible to invest directly in a Proxy Index.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Asset allocations change over time, whereas the allocations and
                the performance used in the hypothetical performance remains
                constant.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Does not account for any transaction costs associated with
                market index performance.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Index performance does not reflect the potential for a more
                focused stock selection available in some strategies, which
                might result in increased volatility within your Recommended
                Portfolio.
              </li>
              <li style={{ marginBottom: '4px' }}>
                The hypothetical performance assumes investment for a specific
                time period, which has been selected by your financial advisor
                in their sole discretion. Performance results for other periods
                will vary.
              </li>
              <li style={{ marginBottom: '4px' }}>
                Strategists&apos; model returns include an allocation to cash of
                up to 2%.
              </li>
              <li style={{ marginBottom: '4px' }}>
                For accounts enrolled in Tax Management Services, the
                account&apos;s holdings and underlying performance may differ
                from the target model holdings and hypothetical performance due
                to on-going tax optimized, discretionary trading.
              </li>
            </ol>
          </SlideParagraph>
        </Grid>
      </Grid>
    );
  },
);

const getUniqueHoldings = (modelSetHoldings: ModelSetHolding) => {
  let rowId = 0;
  return modelSetHoldings.portfolio_options.flatMap(option => {
    const allHoldings = option.portfolios.flatMap(
      (portfolio: any) => portfolio.holdings,
    );

    const uniqueHoldings = allHoldings.reduce((acc: any[], current: any) => {
      const exists = acc.find(
        newHolding =>
          newHolding.model_id === current.model_id ||
          newHolding.strategy === current.strategy,
      );
      if (!exists) {
        acc.push({ ...current, id: rowId++ });
      }
      return acc;
    }, []);

    return uniqueHoldings;
  });
};

export const getRowsDisclosures = (
  modelSetPerformance: ModelSetPerformance[],
  modelSetHoldings: ModelSetHolding,
): {
  id: string;
  full_name: string;
  index_proxy: string;
}[] => {
  const uniqueHoldings = getUniqueHoldings(modelSetHoldings);
  const uniqueEntries = new Map();

  uniqueHoldings.forEach(holding => {
    // Aplanar el array de proxy_metadata de todos los modelos
    const allProxyMetadata = modelSetPerformance.flatMap(
      model => model.proxy_metadata,
    );

    // Filtrar los metadata que coinciden con el holding actual
    const matchingPerformance = allProxyMetadata.filter(
      proxy => proxy.model_id === holding.model_id,
    );

    matchingPerformance.forEach(performance => {
      const key = `${performance.model_id}-${performance.strategy_start_date}`;

      // Solo agregar si no existe esta combinación
      if (!uniqueEntries.has(key)) {
        uniqueEntries.set(key, {
          id: key,
          full_name: `${holding.full_model_name} (${performance.strategy_start_date})`,
          index_proxy: performance.proxy_name || performance.model_id,
        });
      }
    });
  });

  return Array.from(uniqueEntries.values());
};

const columns: GridColDef[] = [
  {
    field: 'full_name',
    headerName: 'STRATEGY(INCEPTION)',
    flex: 1,
    headerAlign: 'left',
    renderHeader: params => (
      <Typography
        style={{
          fontFamily: 'Roboto',
          whiteSpace: 'normal',
          lineHeight: 'normal',
          fontSize: '14px',
          fontWeight: '600',
          color: '#86858B',
        }}
      >
        {params.colDef.headerName}
      </Typography>
    ),
  },
  {
    field: 'index_proxy',
    headerName: 'INDEX PROXY USED PRIOR TO INCEPTION',
    flex: 1,
    headerAlign: 'left',
    renderHeader: params => (
      <Typography
        style={{
          whiteSpace: 'normal',
          lineHeight: 'normal',
          fontSize: '14px',
          fontWeight: '600',
          color: '#86858B',
        }}
      >
        {params.colDef.headerName}
      </Typography>
    ),
  },
];
